import {
  EmailVerificationSendRequestSchema,
  EmailVerificationVerifyRequestSchema,
  normalizeEmail,
  type EmailVerificationOutcome,
  type EmailVerificationSendResponse,
  type EmailVerificationVerifyResponse,
  type PendingEmailChallenge,
} from '@rheora/enrollment-contract';

/**
 * Email-OTP logic for the credit step (E-SF05) — kept OUT of the `.tsx` per
 * CLAUDE.md. Pure, injectable-clock, no React, no network.
 *
 * ── THE ONE BUG THIS FILE EXISTS TO PREVENT ────────────────────────────────
 * A customer verifies `Jane@Example.com`, then edits the email field, then submits.
 * If the SPA still believes "verified", it enables Continue and the server — which
 * gates on the address actually proven — `400`s the most consequential PII endpoint
 * in the funnel. So verification is never a bare boolean: it is always "verified FOR
 * this address", and `isVerificationStaleFor` is the single place that decides.
 *
 * ── AND WHY IT NORMALISES VIA THE CONTRACT ─────────────────────────────────
 * The comparison uses `normalizeEmail` from `@rheora/enrollment-contract` — the same
 * implementation the BFF's verify-store and credit gate use. A locally mirrored
 * trim/lowercase would be one careless edit away from disagreeing with the server,
 * and it would disagree INVISIBLY: every customer whose address has a capital letter
 * would be told to verify an address that looks identical to the one they just
 * verified. One arbiter, imported, not two copies.
 */

/**
 * Which of the five async states the OTP field renders. `error` is the SEND
 * failure state — a verify failure is rendered inside `awaiting-code`, because the
 * customer still has a live challenge and a code to retype.
 */
export type EmailOtpPhase = 'idle' | 'sending' | 'error' | 'awaiting-code' | 'verified';

/**
 * The live challenge, as the SPA knows it. Deliberately does NOT hold the proven
 * address: that lives on the credit form (`IdentityForm.verifiedEmail`) so there is
 * exactly one source of truth for "may this form be submitted?".
 *
 * Every field is server-supplied or server-derived. Nothing here is a locally
 * started timer — see `resendAvailableInSeconds`.
 */
export interface EmailVerificationState {
  /** NORMALISED address the live challenge was issued for; null when none. */
  readonly challengedEmail: string | null;
  /** ISO-8601 with offset — when the issued code stops being accepted. */
  readonly expiresAt: string | null;
  /** ISO-8601 with offset — the earliest instant a resend will not be refused. */
  readonly resendAvailableAt: string | null;
  /** The last `/verify` answer, for the message under the field. */
  readonly lastOutcome: EmailVerificationOutcome | null;
  /** Attempts left on the live challenge, as last reported. */
  readonly attemptsRemaining: number | null;
}

export const noEmailVerificationChallenge: EmailVerificationState = {
  challengedEmail: null,
  expiresAt: null,
  resendAvailableAt: null,
  lastOutcome: null,
  attemptsRemaining: null,
};

/**
 * The challenge state a RETURNING TAB starts from (E-SF19 P3).
 *
 * ── THE DEFECT ─────────────────────────────────────────────────────────────
 * After a refresh the UI forgot an outstanding OTP entirely: it reverted to
 * "Email me a code" with no hint that a valid 10-minute code was already in the
 * customer's inbox. They pressed it, and either hit the 60-second resend floor or
 * spent one of only THREE sends per hour — a budget shared with the victim-side
 * recipient cap. A page refresh cost a third of it.
 *
 * ⚠️ `challengedEmail` STAYS NULL, AND THAT IS THE WHOLE DESIGN. The BFF does not
 * return the pending recipient — an unredeemed challenge's address is UNPROVEN,
 * and `getVerifiedEmail` withholds it precisely so a refresh can never assert
 * "verified" for an address nobody proved. So the phase stays `idle` (see
 * `emailOtpPhase`, which keys on `challengedEmail`) and the customer still retypes
 * their address. What is restored is the two INSTANTS — so the resend countdown is
 * honest — and the fact that a code is outstanding, so the screen can say so.
 */
export function seedChallengeFrom(
  snapshot: { readonly pendingEmailChallenge: PendingEmailChallenge | null } | undefined,
): EmailVerificationState {
  const pending = snapshot?.pendingEmailChallenge ?? null;
  if (pending === null) return noEmailVerificationChallenge;
  return {
    challengedEmail: null,
    expiresAt: pending.expiresAt,
    resendAvailableAt: pending.resendAvailableAt,
    lastOutcome: null,
    attemptsRemaining: pending.attemptsRemaining,
  };
}

/**
 * True when a code the customer has NOT yet redeemed is still live — i.e. pressing
 * "email me a code" would spend a send they do not need.
 *
 * Reads the expiry rather than a boolean so a challenge that lapsed while the tab
 * was closed stops claiming to be outstanding.
 */
export function hasOutstandingCode(state: EmailVerificationState, now: Date = new Date()): boolean {
  if (state.expiresAt === null) return false;
  const expiresMs = Date.parse(state.expiresAt);
  if (Number.isNaN(expiresMs)) return false;
  return expiresMs > now.getTime();
}

/** The two things a `/verify` answer changes, as one value the page can apply. */
export interface VerifyOutcomeApplied {
  readonly challenge: EmailVerificationState;
  /** The NORMALISED proven address, or null when the answer was not `verified`. */
  readonly verifiedEmail: string | null;
}

export interface OtpCodeValidation {
  readonly ok: boolean;
  /** Digits-only, exactly 6 — undefined when invalid. */
  readonly code: string | undefined;
  /** i18n KEY, resolved by the page. */
  readonly error: string | undefined;
}

/**
 * Is a previously proven address no longer the address in the field?
 *
 * `false` when nothing has been proven yet (there is no stale claim to make — the
 * "not verified at all" case is a separate error, not a stale one).
 *
 * ⚠️ BOTH SIDES ARE NORMALISED. `verifiedEmail` is already normalised when it is
 * recorded, but normalising it again is free and makes the function correct for a
 * caller that passes a raw string.
 */
export function isVerificationStaleFor(
  verifiedEmail: string | null,
  currentEmail: string,
): boolean {
  if (verifiedEmail === null) return false;
  return normalizeEmail(verifiedEmail) !== normalizeEmail(currentEmail);
}

/**
 * May we ask the server to mail this address at all? A guard for the Send button,
 * so a typo like `jane@` costs a disabled button rather than a round-trip and one
 * of the customer's three hourly sends. The shared contract schema is the arbiter,
 * so the FE and BFF agree on what a mailable address is.
 */
export function canRequestOtpFor(email: string): boolean {
  return EmailVerificationSendRequestSchema.shape.email.safeParse(email.trim()).success;
}

/**
 * Validate the typed code. Digits are extracted first (a pasted `123 456` is a
 * customer typing what they were sent, not an error), then the shared contract
 * schema has the final word on "6 digits". Errors are i18n keys.
 */
export function validateOtpCode(raw: string): OtpCodeValidation {
  const digits = raw.replace(/\D/gu, '');
  if (digits.length === 0) {
    return { ok: false, code: undefined, error: 'shop.credit.otp.code.required' };
  }
  if (!EmailVerificationVerifyRequestSchema.shape.code.safeParse(digits).success) {
    return { ok: false, code: undefined, error: 'shop.credit.otp.code.invalid' };
  }
  return { ok: true, code: digits, error: undefined };
}

/**
 * Whole seconds until a resend is allowed. `0` means "allowed now".
 *
 * ── DERIVED FROM THE SERVER'S INSTANT, NEVER FROM A LOCAL COUNTDOWN ────────
 * The naive implementation starts a 60-second counter when the send returns and
 * decrements it. That drifts: a backgrounded or OS-suspended tab throttles timers,
 * so the button re-enables late (or, if the tab slept through the whole window,
 * stays disabled while the server would happily accept a resend). Recomputing the
 * remaining time from `resendAvailableAt` on every tick makes the display correct
 * no matter how many ticks were dropped.
 *
 * Rounded UP so a genuinely-blocked resend never reads "0s" — one second of
 * over-statement beats an enabled button the server will refuse.
 */
export function resendAvailableInSeconds(
  resendAvailableAt: string | null,
  now: Date = new Date(),
): number {
  if (resendAvailableAt === null) return 0;
  const targetMs = Date.parse(resendAvailableAt);
  if (Number.isNaN(targetMs)) return 0;
  const remainingMs = targetMs - now.getTime();
  return remainingMs <= 0 ? 0 : Math.ceil(remainingMs / 1_000);
}

/** The challenge state produced by a successful `/send` for `email`. */
export function challengeIssued(
  email: string,
  response: EmailVerificationSendResponse,
): EmailVerificationState {
  return {
    challengedEmail: normalizeEmail(email),
    expiresAt: response.expiresAt,
    resendAvailableAt: response.resendAvailableAt,
    lastOutcome: null,
    attemptsRemaining: null,
  };
}

/**
 * Fold a `/verify` answer into the challenge state, and say which address (if any)
 * is now PROVEN. Only `verified` proves anything; `incorrect`/`expired`/`locked`
 * keep the challenge on screen with an honest message and no proven address.
 */
export function applyVerifyOutcome(
  state: EmailVerificationState,
  email: string,
  response: EmailVerificationVerifyResponse,
): VerifyOutcomeApplied {
  return {
    challenge: {
      ...state,
      lastOutcome: response.status,
      attemptsRemaining: response.attemptsRemaining,
    },
    verifiedEmail: response.status === 'verified' ? normalizeEmail(email) : null,
  };
}

/**
 * Is the address currently in the field the one the OTP proved?
 *
 * A one-line read of the phase, but it has a name and a home because TWO components
 * now render off it: `EmailOtpField` (which drops to the confirmation note) and the
 * tick that sits inside the email input. A bare `phase === 'verified'` in each `.tsx`
 * is exactly how those two drift apart, and a tick that outlives the field's own
 * verified state is a lie about a compliance-relevant fact.
 */
export function isEmailProven(phase: EmailOtpPhase): boolean {
  return phase === 'verified';
}

export interface EmailOtpPhaseInput {
  readonly challenge: EmailVerificationState;
  /** The NORMALISED address proven so far, or null. */
  readonly verifiedEmail: string | null;
  /** The address currently in the email field, raw as typed. */
  readonly email: string;
  readonly isSending: boolean;
  readonly hasSendError: boolean;
}

/**
 * Which of the five states to render.
 *
 * ⚠️ EVERY BRANCH IS TIED TO THE CURRENT ADDRESS, WHICH IS THE WHOLE POINT.
 * `verified` requires the proven address to still BE the address in the field, and
 * `awaiting-code` requires the live challenge to have been issued FOR that address.
 * So editing the email after verifying (or after requesting a code) falls back to
 * `idle` — the field asks for a new code and the page's submit gate re-blocks —
 * without the page needing an effect that reaches in and resets state.
 */
export function emailOtpPhase(input: EmailOtpPhaseInput): EmailOtpPhase {
  const { challenge, verifiedEmail, email, isSending, hasSendError } = input;
  if (verifiedEmail !== null && !isVerificationStaleFor(verifiedEmail, email)) return 'verified';
  if (isSending) return 'sending';
  if (hasSendError) return 'error';
  if (
    challenge.challengedEmail !== null &&
    !isVerificationStaleFor(challenge.challengedEmail, email)
  ) {
    return 'awaiting-code';
  }
  return 'idle';
}

/**
 * How many digits an OTP is. Mirrors `EmailVerificationVerifyRequestSchema`'s
 * `/^\d{6}$/` — the contract remains the authority; this is the UI's copy of the
 * number so the input can cap itself without importing a regex.
 */
export const OTP_CODE_LENGTH = 6;

/**
 * What the OTP input accepts from the browser BEFORE normalisation.
 *
 * ⚠️ IT MUST EXCEED `OTP_CODE_LENGTH`, AND THAT IS THE WHOLE POINT. The field
 * used to be `maxLength={6}`, which silently defeated `validateOtpCode`'s
 * non-digit strip: the verification email groups the code for readability
 * ("650 182"), so a customer who COPIES it pastes 7 characters, the browser
 * truncates to 6 BEFORE any handler runs, and `650 18` strips down to the
 * 5-digit `65018`. They were then told "That code should be 6 digits" about a
 * code they had copied perfectly. Measured in the browser, not theorised.
 *
 * Headroom for separators, not for slop — anything past `OTP_CODE_LENGTH`
 * digits is discarded by `normalizeOtpInput` anyway, so this only has to be
 * wide enough that a formatted code survives long enough to be normalised.
 */
export const OTP_INPUT_MAX_LENGTH = 12;

/**
 * Digits only, capped at the code length — what the field is allowed to hold.
 *
 * Runs on every change so STATE only ever contains digits, whatever arrived:
 * "650 182" from our own email, "650-182" from a client that reformats it, or a
 * stray trailing space from a double-click selection. `validateOtpCode` still
 * strips defensively at submit; this makes the field itself honest, so what the
 * customer SEES after pasting is what will be sent.
 */
export function normalizeOtpInput(raw: string): string {
  return raw.replace(/\D/gu, '').slice(0, OTP_CODE_LENGTH);
}
